import { Badge, Button, Card, EmptyState, Input, SkeletonRows, cn, useToast } from '@ambernord/ui';
import { MessagesSquare, Mic, Paperclip, X } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.js';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';
import {
  type Attachment,
  type ChatLiveEvent,
  type ChatSocketStatus,
  type Message,
  useChatSocket,
} from '../lib/useChatSocket.js';

interface Thread {
  id: string;
  scope: string;
  participants: string[] | null;
}

const LIVE_TONE: Record<ChatSocketStatus, 'slate' | 'green' | 'amber'> = {
  idle: 'slate',
  connecting: 'slate',
  live: 'green',
  reconnecting: 'amber',
};

/** Servera atļautais saraksts (apps/api/src/services/attachments.ts) — spoguļots `accept` atribūtā. */
const ATTACHMENT_ACCEPT =
  'application/pdf,image/png,image/jpeg,image/webp,audio/webm,audio/ogg,audio/mpeg,audio/mp4';

/** Deterministiska nokrāsa no authorId — konsekventa krāsa katram autoram (bez identitātes!). */
function authorHue(authorId: string): number {
  let h = 0;
  for (let i = 0; i < authorId.length; i++) {
    h = (h * 31 + authorId.charCodeAt(i)) >>> 0;
  }
  return h % 360;
}

/** Īss anonīms marķējums — tikai tas, ko API atklāj (ID prefikss, §7 robeža paliek neskarta). */
function shortLabel(authorId: string): string {
  return `${authorId.slice(0, 8)}…`;
}

function Avatar({ authorId }: { authorId: string }) {
  const hue = authorHue(authorId);
  return (
    <span
      aria-hidden
      className="flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-full text-xs font-bold uppercase text-white"
      style={{ backgroundColor: `hsl(${hue} 55% 45%)` }}
    >
      {authorId.slice(0, 2)}
    </span>
  );
}

function attachErrorKey(status: number): string {
  switch (status) {
    case 413:
      return 'toast.attachTooLarge';
    case 415:
      return 'toast.attachUnsupported';
    case 503:
      return 'toast.attachStorageUnavailable';
    default:
      return 'toast.attachFailed';
  }
}

function AttachmentList({ items, own }: { items: Attachment[]; own: boolean }) {
  const { t } = useTranslation('chat');
  if (items.length === 0) return null;
  return (
    <ul aria-label={t('attachmentsLabel')} className="mt-1.5 flex flex-col gap-1">
      {items.map((a) => {
        const Icon = a.category === 'voice' ? Mic : Paperclip;
        return (
          <li key={a.id}>
            <a
              href={`/api/chat/attachments/${a.id}`}
              target="_blank"
              rel="noreferrer"
              className={cn(
                'inline-flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-xs underline-offset-2 hover:underline',
                own
                  ? 'border-brand-200 bg-white/60 dark:border-brand-800 dark:bg-slate-900/40'
                  : 'border-slate-200 bg-white/60 dark:border-slate-700 dark:bg-slate-900/40',
              )}
            >
              <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">
                {a.category === 'voice' ? t('voiceNote') : a.filename}
              </span>
            </a>
          </li>
        );
      })}
    </ul>
  );
}

export default function Chat() {
  const { t, i18n } = useTranslation('chat');
  const { toast } = useToast();
  const { user } = useAuth();
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [error, setError] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [newScope, setNewScope] = useState('general');
  const fileInput = useRef<HTMLInputElement>(null);

  const activeIdRef = useRef<string | null>(null);

  const loadThreads = useCallback(async () => {
    const res = await api.get<{ items: Thread[] }>('/api/chat/threads');
    if (!res.ok) {
      setError(true);
      return;
    }
    const items = res.data.items ?? [];
    setThreads(items);
    // Funkcionāls atjauninājums — novēlota atbilde nedrīkst pārrakstīt lietotāja izvēli.
    setActiveId((current) => current ?? items[0]?.id ?? null);
  }, []);

  const loadMessages = useCallback(async (id: string) => {
    const res = await api.get<{ items: Message[] }>(`/api/chat/threads/${id}/messages`);
    // Tukšs pavediens atbild ātrāk par garu — cita pavediena novēlotu sarakstu ignorē.
    if (res.ok && activeIdRef.current === id) setMessages(res.data.items ?? []);
  }, []);

  /** Dedublē pēc id — tā pati ziņa nāk gan no POST atbildes, gan pa ligzdu. */
  const appendMessage = useCallback((message: Message) => {
    setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
  }, []);

  const onLiveEvent = useCallback(
    (event: ChatLiveEvent) => {
      if (event.type === 'message') {
        appendMessage(event.message);
        return;
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === event.messageId
            ? {
                ...m,
                attachments: [
                  ...(m.attachments ?? []).filter((a) => a.id !== event.attachment.id),
                  event.attachment,
                ],
              }
            : m,
        ),
      );
    },
    [appendMessage],
  );

  const liveStatus = useChatSocket(activeId, {
    onEvent: onLiveEvent,
    onReconnect: () => {
      if (activeId) void loadMessages(activeId);
    },
  });

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    activeIdRef.current = activeId;
    setMessages([]);
    if (activeId) void loadMessages(activeId);
  }, [activeId, loadMessages]);

  async function createThread() {
    const res = await api.post<{ thread?: Thread }>('/api/chat/threads', { scope: newScope });
    if (res.ok && res.data.thread) {
      toast(t('toast.threadCreated'));
      await loadThreads();
      setActiveId(res.data.thread.id);
    } else {
      toast(t('toast.createFailed'), 'error');
    }
  }

  function clearFile() {
    setPendingFile(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  /** Ziņojums vispirms, pielikums pēc tam — tikai autors drīkst pievienot, tāpēc tas pats lietotājs. */
  async function send(e: FormEvent) {
    e.preventDefault();
    const body = draft.trim() || pendingFile?.name;
    if (!activeId || !body || sending) return;
    setSending(true);
    try {
      const res = await api.post<{ message?: Message }>(`/api/chat/threads/${activeId}/messages`, {
        body,
      });
      if (!res.ok || !res.data.message) {
        toast(t('toast.sendFailed'), 'error');
        return;
      }
      setDraft('');
      appendMessage({ ...res.data.message, attachments: [] });
      if (pendingFile) {
        const up = await api.upload(
          `/api/chat/threads/${activeId}/messages/${res.data.message.id}/attachments`,
          pendingFile,
          { filename: pendingFile.name },
        );
        if (up.ok) {
          toast(t('toast.attached'));
          clearFile();
        } else {
          toast(t(attachErrorKey(up.status)), 'error');
        }
      }
      // Bez dzīvās ligzdas (pielikumu) atjauninājumi nāk tikai pa REST.
      if (liveStatus !== 'live') await loadMessages(activeId);
    } finally {
      setSending(false);
    }
  }

  return (
    <AppLayout title={t('title')}>
      <div className="grid gap-4 md:grid-cols-[16rem_1fr]">
        <Card className="p-3">
          <div className="mb-3 flex gap-2">
            <Input
              value={newScope}
              onChange={(e) => setNewScope(e.target.value)}
              className="h-8 min-w-0 flex-1 px-2"
              placeholder={t('scopePlaceholder')}
            />
            <Button size="sm" onClick={() => void createThread()}>
              +
            </Button>
          </div>
          {error ? (
            <DataState loading={false} error={t('error')} empty={false}>
              {null}
            </DataState>
          ) : threads === null ? (
            <SkeletonRows rows={3} />
          ) : threads.length === 0 ? (
            <EmptyState
              className="px-3 py-6"
              title={t('emptyThreads.title')}
              hint={t('emptyThreads.hint')}
            />
          ) : (
            <ul className="flex flex-col gap-1">
              {threads.map((th) => (
                <li key={th.id}>
                  <button
                    type="button"
                    onClick={() => setActiveId(th.id)}
                    className={`w-full truncate rounded-md px-3 py-1.5 text-left text-sm ${
                      activeId === th.id
                        ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {th.scope}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="flex min-h-[24rem] flex-col">
          {activeId && (
            <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-2 dark:border-slate-800">
              <span data-testid="chat-thread-scope" className="truncate text-sm font-medium">
                {threads?.find((th) => th.id === activeId)?.scope}
              </span>
              <Badge tone={LIVE_TONE[liveStatus]} data-testid="chat-live" data-state={liveStatus}>
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
                {t(`live.${liveStatus}`)}
              </Badge>
            </div>
          )}
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {activeId === null ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('selectThread')}</p>
            ) : messages.length === 0 ? (
              <EmptyState
                icon={<MessagesSquare className="h-6 w-6" />}
                title={t('noMessages')}
                hint={t('noMessagesHint')}
                className="py-8"
              />
            ) : (
              messages.map((m) => {
                const own = m.authorId !== null && user !== null && m.authorId === user.id;
                return (
                  <div
                    key={m.id}
                    className={cn('flex items-end gap-2', own ? 'flex-row-reverse' : 'flex-row')}
                  >
                    {m.authorId ? (
                      <Avatar authorId={m.authorId} />
                    ) : (
                      <span
                        aria-hidden
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-300 text-xs font-bold text-white dark:bg-slate-700"
                      >
                        ?
                      </span>
                    )}
                    <div
                      className={cn(
                        'max-w-[75%] rounded-lg px-3 py-2 text-sm',
                        own
                          ? 'bg-brand-50 text-slate-900 dark:bg-brand-900/40 dark:text-slate-100'
                          : 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100',
                      )}
                    >
                      <p
                        className={cn(
                          'mb-0.5 flex items-baseline gap-2 text-xs text-slate-400 dark:text-slate-500',
                          own && 'flex-row-reverse',
                        )}
                      >
                        <span className="font-medium">
                          {own ? t('you') : m.authorId ? shortLabel(m.authorId) : '—'}
                        </span>
                        <span>{formatDateTime(m.createdAt, i18n.language)}</span>
                      </p>
                      <p className="whitespace-pre-wrap break-words">{m.body}</p>
                      <AttachmentList items={m.attachments ?? []} own={own} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {activeId && (
            <form
              onSubmit={(e) => void send(e)}
              className="flex flex-col gap-2 border-t border-slate-200 p-3 dark:border-slate-800"
            >
              {pendingFile && (
                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                  <Paperclip aria-hidden className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{pendingFile.name}</span>
                  <button
                    type="button"
                    onClick={clearFile}
                    aria-label={t('removeAttachment')}
                    className="rounded p-0.5 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <X aria-hidden className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  accept={ATTACHMENT_ACCEPT}
                  className="hidden"
                  onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={t('attach')}
                  title={t('attach')}
                  onClick={() => fileInput.current?.click()}
                  className="px-2"
                >
                  <Paperclip aria-hidden className="h-4 w-4" />
                </Button>
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={t('messagePlaceholder')}
                  className="min-w-0 flex-1"
                />
                <Button type="submit" disabled={sending || (!draft.trim() && !pendingFile)}>
                  {t('send')}
                </Button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}
